import React, { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { childrenSelectWarning, maxFlightChildAge, maxFlightInfantAge, maxFlightPassengerCount, maxInfantsWarning } from 'constants/flight'
import { validateOccupants } from 'lib/flights'
import OccupancyItem from 'components/Common/OccupancyPicker/OccupancyItem'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import SearchFloatingPanel, { SearchFloatingPanelProps } from 'components/SearchV2/Components/Mobile/SearchFloatingPanel/SearchFloatingPanel'
import TravellerWarningMessage from './TravellerWarningMessage'

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(20)};
`

const Warning = styled(TravellerWarningMessage)`
  margin-top: ${rem(16)};
`

const StyledOccupancyItem = styled(OccupancyItem)`
  border: none;
  box-shadow: none;
  border-radius: ${props => props.theme.borderRadius.large};

  > div {
    border: none;
    box-shadow: none;
    border-radius: ${props => props.theme.borderRadius.large};
  }
`

interface Props extends
  Omit<
    SearchFloatingPanelProps,
    'title' | 'isConfirmButtonHidden' | 'children'
  >
{
}

const FlightTravellerFloatingPanel = (props: Props) => {
  const { occupants } = useContext(FlightSearchWidgetStateContext)
  const flightSearchDispatch = useContext(FlightSearchWidgetDispatchContext)

  const onChangeOccupants = useCallback((_, nextOccupancy: App.Occupants) => {
    flightSearchDispatch({ type: FlightSearchWidgetActions.SET_OCCUPANTS, occupants: nextOccupancy })
  }, [flightSearchDispatch])

  const isInfantsAdded = occupants.childrenAge.some((age) => age !== -1 && age <= maxFlightInfantAge)

  const capacity = useMemo(() => ({
    adults: maxFlightPassengerCount - occupants.childrenAge.length,
    children: maxFlightPassengerCount - occupants.adults,
    infants: maxFlightPassengerCount - occupants.adults,
  }), [occupants])

  const invalidSelection = useMemo(() => validateOccupants(occupants), [occupants])

  return (
    <SearchFloatingPanel
      {...props}
      title="Who is going?"
    >
      <ContentContainer>
        <StyledOccupancyItem
          itemId={0}
          occupancy={occupants}
          onChange={onChangeOccupants}
          maxChildAge={maxFlightChildAge}
          maxInfantAge={maxFlightInfantAge}
          childrenAgeLabel="at time of return flight"
          capacities={[capacity]}
        />
        {isInfantsAdded && !invalidSelection && <Warning message={childrenSelectWarning} />}
        {invalidSelection && <Warning message={maxInfantsWarning} />}
      </ContentContainer>
    </SearchFloatingPanel>
  )
}

export default FlightTravellerFloatingPanel
